import { useState, useRef, useEffect } from 'react'
import { api } from '../lib/api'
import { toast } from 'react-toastify'

export interface WorkerProgressConfig {
  brandId: string
  statusEndpoint: string
  pollInterval?: number
  pollTimeout?: number
  onComplete?: () => void
  completionMessage?: string
  showToast?: boolean
}

export interface WorkerProgressState {
  status: 'idle' | 'active'
  message: string
  progress?: {
    processed: number
    total: number
    percent: number
  }
}

/**
 * Custom hook for managing background worker progress polling
 *
 * Handles:
 * - Polling worker status endpoint
 * - Showing progress updates
 * - Completing with toast notification
 * - Preventing duplicate completion toasts
 * - Automatic cleanup on unmount
 */
export function useWorkerProgress(config: WorkerProgressConfig) {
  const {
    brandId,
    statusEndpoint,
    pollInterval = 5000,
    pollTimeout = 300000, // 5 minutes default
    onComplete,
    completionMessage = 'Operation complete!',
    showToast = true
  } = config

  const [state, setState] = useState<WorkerProgressState>({
    status: 'idle',
    message: ''
  })

  const pollIntervalRef = useRef<number | null>(null)
  const pollTimeoutRef = useRef<number | null>(null)
  const hasShownCompleteRef = useRef(false)

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current)
      pollTimeoutRef.current = null
    }
  }

  const updateProgress = async () => {
    // If we've already shown completion, don't process anymore
    if (hasShownCompleteRef.current) {
      return
    }

    try {
      const response = await api.get(statusEndpoint, {
        params: { brandId }
      })

      // If worker is still active, update progress message
      if (response.data.active) {
        setState({
          status: 'active',
          message: response.data.message || 'Processing...',
          progress: response.data.progress
        })
      } else {
        // Worker is complete - set flag FIRST before anything else (using ref for immediate update)
        hasShownCompleteRef.current = true

        // Stop polling
        stopPolling()
        setState({
          status: 'idle',
          message: ''
        })

        // Show completion toast (if enabled)
        if (showToast) {
          toast.success(completionMessage, {
            position: 'top-right',
            autoClose: 3000,
          })
        }

        // Call completion callback
        if (onComplete) {
          await onComplete()
        }
      }
    } catch (error) {
      console.error('[useWorkerProgress] Failed to update progress:', error)
    }
  }

  const startPolling = (initialMessage?: string) => {
    // Reset completion flag
    hasShownCompleteRef.current = false

    // Set initial state
    setState({
      status: 'active',
      message: initialMessage || 'Processing...'
    })

    // Immediately call once to get initial progress
    updateProgress()

    // Start polling interval
    const interval = setInterval(() => {
      updateProgress()
    }, pollInterval)
    pollIntervalRef.current = interval

    // Stop polling after timeout (fallback)
    const timeout = setTimeout(() => {
      if (pollIntervalRef.current) {
        stopPolling()
        setState({
          status: 'idle',
          message: ''
        })

        // Refresh data on timeout as well
        if (!hasShownCompleteRef.current) {
          hasShownCompleteRef.current = true
          if (showToast) {
            toast.success(completionMessage, {
              position: 'top-right',
              autoClose: 3000,
            })
          }
          if (onComplete) {
            onComplete()
          }
        }
      }
    }, pollTimeout)
    pollTimeoutRef.current = timeout
  }

  const checkStatus = async () => {
    try {
      const response = await api.get(statusEndpoint, {
        params: { brandId }
      })
      if (response.data.active) {
        // There are active jobs, start polling
        setState({
          status: 'active',
          message: response.data.message || 'Processing...',
          progress: response.data.progress
        })
        startPolling(response.data.message)
      }
    } catch (error) {
      console.error('[useWorkerProgress] Failed to check status:', error)
    }
  }

  const dismiss = () => {
    stopPolling()
    setState({
      status: 'idle',
      message: ''
    })
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling()
    }
  }, [])

  return {
    state,
    startPolling,
    stopPolling,
    checkStatus,
    dismiss
  }
}
